import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const experiences = [
  {
    year: "Jan 2024 - Present",
    role: "AI Product & Partnerships",
    company: "Climformatics Inc.",
    description: "Leading the product lifecycle for the Energy Utilities Early Alert System (EAS), managing a cross-functional team to deliver Fire-Danger and Heat Stress forecasting modules.",
    achievements: [
      "Secured $600k+ in non-dilutive funding (CalSEED, DOE) by translating feedback into validated features",
      "Validated 'Data Analytics as a Service' model, defining a pipeline projected to generate $2.5M in annual revenue",
      "Successfully launched 4 active pilots and advanced Technology Readiness Level (TRL) from 2 to 6"
    ]
  },
  {
    year: "Sep 2022 - Aug 2023",
    role: "Product Consultant",
    company: "SWiiFT",
    description: "Documented business requirements into detailed functional specifications and analyzed market trends to recommend technology solutions.",
    achievements: [
      "Achieved a 5% reduction in development expenses and reduced project timelines by 10% via agile planning",
      "Devised a 50% more efficient product launch strategy by collaborating with leadership on IP strategies",
      "Led 15+ multivariate tests and synthesized results from 20+ experiments to inform launch decisions"
    ]
  },
  {
    year: "Jan 2022 - May 2023",
    role: "Co-Instructor & Board Member",
    company: "SRM Innovation Centre",
    description: "Served as Co-Instructor for the Design and Innovation course, guiding students through hands-on projects applying design thinking and prototyping.",
    achievements: [
      "Collaborated with industry experts to refine curriculum, ensuring alignment with innovation best practices",
      "Integrated experiential learning components and startup case studies into the syllabus",
      "Facilitated workshops and mentored student teams on product development from ideation to execution"
    ]
  },
  {
    year: "Jan 2022 - Jun 2022",
    role: "Analyst",
    company: "Deloitte",
    description: "Developed and implemented innovative full stack webapp and website updates on SharePoint while conducting engineering reviews.",
    achievements: [
      "Increased Net Promoter Score (NPS) by 50% (from 40 to 60) in 6 months",
      "Boosted team efficiency by 20% through digital solutions like gamified platforms and booking systems",
      "Authored approved engineering review document (ERD) for the backend plugin migration process"
    ]
  },
  {
    year: "Jan 2021 - Dec 2021",
    role: "Associate Product Manager",
    company: "Momento Frames",
    description: "Streamlined the Agile environment for engineers using Jira and Kanban to develop scalable eCommerce platforms.",
    achievements: [
      "Reduced operational expenses by $56,000 (18% reduction) within the first quarter via customer segmentation",
      "Developed admin/manufacturer dashboards and integrated Google Analytics and white-label solutions",
      "Conducted 100+ customer interviews to identify cost-saving opportunities"
    ]
  },
  {
    year: "Jun 2018 - Dec 2018",
    role: "Co-Founder",
    company: "Converge",
    description: "Built a comprehensive platform to empower high school students by merging academic learning with practical skills and industry exposure.",
    achievements: [
      "Created ML-powered pathways connecting education, training, and mentorship into a seamless journey",
      "Established a nationwide pool of mentors matched to students using AI",
      "Implemented predictive analytics to help students identify future career paths and skill gaps"
    ]
  }
];

const Experience = () => {
  return (
    <section id="experience" className="bg-white border-b border-slate-200 pt-8 md:pt-16 pb-0">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-y-8">
        
        {/* --- LEFT COLUMN: Title (Sticky) --- */}
        <div className="lg:col-span-4 lg:border-r lg:border-slate-200 p-6 md:p-12 flex flex-col justify-between bg-slate-50/50">
           {/* Make title non-sticky on small screens for better mobile flow */}
           <div className="lg:sticky lg:top-0 lg:h-screen">
             <div className="flex flex-col h-full justify-between">
               <div>
                 <div className="flex items-center gap-4 mb-6">
                   <span className="h-px w-8 bg-blue-600"></span>
                   <span className="text-blue-600 font-bold tracking-widest uppercase text-xs">Experience</span>
                 </div>
                 <h2 className="text-3xl md:text-5xl font-bold text-slate-900 tracking-tight leading-[0.9]">
                   My<br />Journey.
                 </h2>
               </div>

               {/* Decorative bottom text */}
               <div className="hidden lg:block text-xs font-mono text-slate-400 uppercase tracking-widest mt-6">
                 Scroll to explore <br /> professional history
               </div>
             </div>
           </div>
        </div>

        {/* --- RIGHT COLUMN: The Timeline --- */}
        <div className="lg:col-span-8">
          {experiences.map((item, index) => (
            <TimelineItem key={index} item={item} index={index} />
          ))}
        </div>

      </div>
    </section>
  );
};

const TimelineItem = ({ item, index }) => {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: false, margin: "-10%" }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="group relative border-b border-slate-200 last:border-b-0 hover:bg-slate-50 transition-colors duration-500"
    >
      <div className="relative px-4 py-8 md:p-16 grid md:grid-cols-12 gap-6 items-start">

        {/* 1. The Year (becomes top-aligned on small screens) */}
        <div className="md:col-span-3 mb-4 md:mb-0">
            <span className="inline-block px-3 py-1 rounded-full border border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-500 group-hover:bg-blue-600 group-hover:text-white group-hover:border-blue-600 transition-all duration-300">
                {item.year}
            </span>
        </div>

        {/* 2. The Role & Company */}
        <div className="md:col-span-9">
            <h3 className="text-xl md:text-3xl font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors duration-300">
                {item.role}
            </h3>
            <div className="text-sm md:text-lg font-medium text-slate-400 mb-6 md:mb-8 group-hover:text-slate-600 transition-colors">
                {item.company}
            </div>

            <p className="text-slate-500 leading-relaxed mb-6 text-base md:text-lg font-light">
                {item.description}
            </p>

            {/* The Achievements List */}
            <ul className="space-y-3">
                {item.achievements.map((point, i) => (
                    <li key={i} className="flex items-start gap-3 text-slate-500 group-hover:text-slate-900 transition-colors text-sm md:text-base">
                        <span className="mt-1 w-2 h-2 rounded-full bg-slate-300 group-hover:bg-blue-600 transition-colors" />
                        <span className="font-medium leading-relaxed">{point}</span>
                    </li>
                ))}
            </ul>
        </div>

      </div>
    </motion.div>
  );
}

export default Experience;